import cx from 'classnames'
import { Form } from '../../Form/Form'
import { ArrowLeft, ArrowRight } from 'almanac-ui/icons'
import { Button } from 'blaze-ui/Button'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { Input } from 'almanac-ui/TextInputField/Input'
import Styles from './SelectContent.module.scss'
import { pluralize } from 'utils'
import { ScheduledContent } from '../ScheduleContent/ScheduleContent'
import { Skeleton } from 'antd'
import { Plus, Trash2 } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export type GeneratedContent = Omit<ScheduledContent, 'postAt' | 'hasReplaceableImage'>

const LoadingSkeleton = ({ count, rows }: { count: number; rows: number }) => {
  const skeletons = Array(count).fill(count)

  return (
    <>
      {skeletons.map((_, index) => {
        return (
          <div
            data-testid={`skeleton-${index}`}
            key={index}
            className={cx(Styles.contentContainer, Styles.skeletonContentContainer)}
          >
            <div className={Styles.checkboxContainer}>
              <Checkbox size="lg" onChange={() => {}} isSelected={false} isDisabled />
            </div>
            <Skeleton className={Styles.skeleton} active title={false} paragraph={{ rows, width: '100%' }} />
          </div>
        )
      })}
    </>
  )
}

interface Props {
  header?: JSX.Element | null
  onSubmit: () => void
  goBack: () => void
  generateMore: () => void
  generatedContent: GeneratedContent[]
  setGeneratedContent: (content: GeneratedContent[]) => void
  selectedContent: string[]
  setSelectedContent: (uuids: string[]) => void
  isLoading: boolean
  inputPlaceholder: string
  skeletonProps: {
    count: number
    rows: number
  }
  isTextarea?: boolean
  selectionLimit?: number
  previousLabel?: string
  nextLabel?: (count: number) => string
  contentType?: string
  isSubmitDisabled?: boolean
}

export const SelectContent = ({
  header = null,
  onSubmit,
  goBack,
  generateMore,
  isLoading,
  generatedContent,
  setGeneratedContent,
  selectedContent,
  setSelectedContent,
  inputPlaceholder,
  skeletonProps,
  isTextarea = true,
  selectionLimit = 10,
  previousLabel = 'Previous Step',
  nextLabel = (count: number) => `Plan ${count} ${pluralize('Post', count)} on a Calendar`,
  contentType = 'Captions',
  isSubmitDisabled = false,
}: Props) => {
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const { isMobile } = useResponsiveDesign()

  const toggleContent = (uuid: string) => {
    if (selectedContent.includes(uuid)) {
      setSelectedContent(selectedContent.filter((u) => u !== uuid))
      return
    }

    setSelectedContent([...selectedContent, uuid])
  }

  const updateContent = (newValue: string, uuid: string) => {
    const newContent = generatedContent.map((c) => {
      if (c.uuid === uuid) return { ...c, text: newValue }
      return c
    })
    setGeneratedContent(newContent)
  }

  const removeContent = (uuid: string) => {
    const newContent = generatedContent.filter((c) => c.uuid !== uuid)
    const newSelectedContent = selectedContent.filter((selectedUuid) => selectedUuid !== uuid)

    setGeneratedContent(newContent)
    setSelectedContent(newSelectedContent)
  }

  const toggleSelectAll = () => {
    if (selectedContent.length === generatedContent.length || selectedContent.length === selectionLimit) {
      setSelectedContent([])
      return
    }

    if (selectedContent.length === selectionLimit) {
      return
    }

    const ids = generatedContent.map((gc) => gc.uuid)
    setSelectedContent(ids)
  }

  const selectAllPrimaryAction = () => {
    return (
      <div className={Styles.selectAllPrimaryAction}>
        <Checkbox
          data-testid="select-all-primary-actions"
          size="lg"
          onChange={toggleSelectAll}
          isSelected={selectedContent.length > 0 && selectedContent.length === generatedContent.length}
          aria-label="Select all"
          isDisabled={!generatedContent.length}
        >
          <div className={Styles.text}>
            <Text variant="largeList">{selectedContent.length < selectionLimit ? 'Select All' : 'Deselect All'}</Text>
          </div>
        </Checkbox>
      </div>
    )
  }

  const submitLabel = nextLabel(selectedContent.length)

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields>
            <div className={Styles.wrapper}>
              {header}
              <div className={Styles.selectAllContainer}>
                <Checkbox
                  data-testid="select-all"
                  size="lg"
                  onChange={toggleSelectAll}
                  isSelected={selectedContent.length > 0 && selectedContent.length === generatedContent.length}
                  aria-label="Select all"
                  isDisabled={!generatedContent.length}
                >
                  <span className={Styles.selectAllText}>
                    {selectedContent.length < selectionLimit ? 'Select All ' : 'Deselect All '} {contentType}
                  </span>
                </Checkbox>
              </div>
              {generatedContent.map(({ text, uuid }, index) => {
                return (
                  <div
                    className={cx(Styles.contentContainer, { [Styles.isTextarea]: isTextarea })}
                    key={`content-${uuid}`}
                  >
                    <div className={Styles.checkboxContainer}>
                      <Checkbox
                        data-testid={`checkbox-${uuid}`}
                        size="lg"
                        onChange={() => toggleContent(uuid)}
                        isSelected={selectedContent.includes(uuid)}
                        isDisabled={!selectedContent.includes(uuid) && selectedContent.length >= selectionLimit}
                        aria-label={text}
                      />
                    </div>
                    <Input
                      className={Styles.input}
                      data-testid={`text-input-${uuid}`}
                      isTextarea={isTextarea}
                      scrollable={false}
                      size="lg"
                      name={`Content ${index}`}
                      value={text}
                      onChange={(e) => updateContent(e.target.value, uuid)}
                      autoFocus={text === ''}
                      aria-label="Generated Content"
                      placeholder={inputPlaceholder}
                      onKeyDown={(e) => {
                        // Prevent up and down from selecting other radio options
                        e.stopPropagation()
                      }}
                    />
                    <IconButton
                      size="xs"
                      data-testid={`remove-button-${uuid}`}
                      variant="ghost"
                      icon={Trash2}
                      onPress={() => removeContent(uuid)}
                    />
                  </div>
                )
              })}
              {isLoading && <LoadingSkeleton {...skeletonProps} />}
              <div className={Styles.generateMoreButtonContainer}>
                <Button size="lg" frontIcon={Plus} variant="tertiary" onPress={generateMore}>
                  Generate More
                </Button>
              </div>
            </div>
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        {!isMobile && (
          <Form.PreviousButton variant={isDesktopWizardsUpdatesOn ? 'editor' : 'secondary'} onPress={goBack}>
            Previous Step
          </Form.PreviousButton>
        )}
        <Form.PrimaryActions>
          {!isMobile ? (
            <>
              {selectAllPrimaryAction()}
              <Button
                size="lg"
                onPress={onSubmit}
                endIcon={ArrowRight}
                isDisabled={isSubmitDisabled || selectedContent.length === 0}
                aria-label={submitLabel}
              >
                {submitLabel}
              </Button>
            </>
          ) : (
            <>
              <div className={Styles.mobilePrimaryActions}>
                <IconButton icon={ArrowLeft} variant="tertiary" size="lg" onPress={goBack} />
                <Button
                  size="lg"
                  onPress={onSubmit}
                  endIcon={ArrowRight}
                  isDisabled={isSubmitDisabled || selectedContent.length === 0}
                  fullWidth
                  aria-label={submitLabel}
                >
                  {submitLabel}
                </Button>
              </div>
              {selectAllPrimaryAction()}
            </>
          )}
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
